import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, MenuIcon, XIcon } from 'lucide-react';
import { Logo } from '../shell/Logo';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../utils/cn';

/**
 * Design B navigation.
 *
 * It begins over the photograph — inverted, weightless, part of the image —
 * and resolves into a solid warm-white bar once the hero has passed. The change
 * is the smallest one that keeps the labels legible against whatever is behind
 * them; nothing about the navigation itself moves.
 */
interface MenuEntry {
  label: string;
  to?: string;
  items?: {
    label: string;
    to: string;
    note: string;
  }[];
}
const PRIMARY: MenuEntry[] = [{
  label: 'Product',
  items: [{
    to: '/academics',
    label: 'Academics',
    note: 'Courses, timetable and results'
  }, {
    to: '/housing',
    label: 'Housing',
    note: 'Verified places near campus'
  }, {
    to: '/market',
    label: 'Marketplace',
    note: 'Buy, sell, hire and earn'
  }, {
    to: '/money',
    label: 'Money',
    note: 'Records, splits and bills'
  }]
}, {
  label: 'For students',
  items: [{
    to: '/register',
    label: 'Create an account',
    note: 'One account, every campus surface'
  }, {
    to: '/discover',
    label: 'Discover universities',
    note: 'Programmes and funding'
  }, {
    to: '/opportunities',
    label: 'Opportunities',
    note: 'Jobs, gigs and internships'
  }]
}, {
  label: 'For campuses',
  to: '/welcome/campuses'
}, {
  label: 'Marketplace',
  to: '/market'
}, {
  label: 'Resources',
  items: [{
    to: '/support',
    label: 'Help centre',
    note: 'Guides and answers'
  }, {
    to: '/legal/privacy',
    label: 'Privacy',
    note: 'What we hold, and what we never do'
  }, {
    to: '/network',
    label: 'Campus representatives',
    note: 'Field operator programme'
  }]
}];
function Dropdown({
  entry,
  inverted
}: {
  entry: MenuEntry;
  inverted: boolean;
}) {
  const [open, setOpen] = useState(false);
  return <li className="relative shrink-0" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)} data-id="element-2404" data-mp="3pwak7xqg2z33">
      <button type="button" aria-expanded={open} onClick={() => setOpen(current => !current)} onKeyDown={event => event.key === 'Escape' && setOpen(false)} className={cn('flex min-h-touch items-center gap-1 rounded-md px-3 text-body-sm font-medium transition-colors duration-150 ease-out', inverted ? 'text-white/85 hover:text-white' : 'text-ink-muted hover:text-ink')} data-id="element-2405" data-mp="3uniyb72pz8ml">
        {entry.label}
        <ChevronDownIcon aria-hidden className={cn('h-4 w-4 transition-transform duration-150 ease-out', open && 'rotate-180')} data-id="element-2406" data-mp="3h7q8qzal7lr" />
      </button>

      {open && entry.items && <div className="absolute left-0 top-full z-40 w-80 pt-2" data-id="element-2407" data-mp="2o6f8255ieb5h">
          <ul className="overflow-hidden rounded-card border border-line bg-surface p-2 shadow-[0_18px_44px_rgba(15,23,42,0.16)]" data-id="element-2408" data-mp="2s8abpgawx0mf">
            {entry.items.map(item => <li key={item.label} data-id="element-2409" data-mp="2kquaj6so3u7h">
                <Link to={item.to} onClick={() => setOpen(false)} className="block rounded-lg px-3 py-2.5 transition-colors duration-150 ease-out hover:bg-canvas" data-id="element-2410" data-mp="2fq687oyznqxy">
                  <span className="block text-body-sm font-semibold text-ink" data-id="element-2411" data-mp="2pp5ifbzejun2">{item.label}</span>
                  <span className="mt-0.5 block text-caption text-ink-muted" data-id="element-2412" data-mp="21z1ibelfad0t">{item.note}</span>
                </Link>
              </li>)}
          </ul>
        </div>}
    </li>;
}
export function HeaderB() {
  const {
    authenticated,
    identity
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [solid, setSolid] = useState(false);
  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 120);
    onScroll();
    window.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const inverted = !solid && !open;
  return <header className={cn('fixed inset-x-0 top-0 z-40 transition-colors duration-200 ease-out', inverted ? 'bg-gradient-to-b from-black/55 to-transparent' : 'bg-surface/95 shadow-[0_1px_0_rgba(15,23,42,0.08)] backdrop-blur')} data-id="element-2413" data-mp="hfh8sqw15z9d">
      <div className="mx-auto flex max-w-[88rem] items-center gap-4 px-4 py-3 sm:px-8" data-id="element-2414" data-mp="varwnkbuxk48">
        <Link to="/welcome" className="shrink-0 rounded-md" aria-label="Campuzon NG home" data-id="element-2415" data-mp="107id48rkqhq6">
          <Logo className={inverted ? 'brightness-0 invert' : undefined} data-id="element-2416" data-mp="155bi4yh555ov" />
        </Link>

        <nav aria-label="Campuzon" className="hidden min-w-0 flex-1 justify-center xl:flex" data-id="element-2417" data-mp="199rlarzddz0e">
          <ul className="flex items-center gap-0.5" data-id="element-2418" data-mp="1e69ohnf1xpam">
            {PRIMARY.map(entry => entry.items ? <Dropdown key={entry.label} entry={entry} inverted={inverted} data-id="element-2419" data-mp="3b1nv9x5w30t2" /> : <li key={entry.label} className="shrink-0" data-id="element-2420" data-mp="3fz64zprqdk24">
                  <Link to={entry.to as string} className={cn('flex min-h-touch items-center rounded-md px-3 text-body-sm font-medium transition-colors duration-150 ease-out', inverted ? 'text-white/85 hover:text-white' : 'text-ink-muted hover:text-ink')} data-id="element-2421" data-mp="38gghfobzwtwd">
                    {entry.label}
                  </Link>
                </li>)}
          </ul>
        </nav>

        <div className="ml-auto hidden items-center gap-2 xl:flex" data-id="element-2422" data-mp="3mgheesp53h6k">
          {authenticated ? <Link to="/" className="inline-flex min-h-touch items-center rounded-lg bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 hover:text-white" data-id="element-2423" data-mp="3heiczocymuc9">
              {identity ? `Continue as @${identity.username}` : 'Go to Campuzon'}
            </Link> : <>
              <Link to="/signin" className={cn('flex min-h-touch items-center rounded-md px-3 text-body-sm font-medium transition-colors duration-150 ease-out', inverted ? 'text-white hover:text-white/80' : 'text-ink hover:text-brand-700')} data-id="element-2424" data-mp="23htas3ft74q3">
                Sign in
              </Link>
              <Link to="/register" className="inline-flex min-h-touch items-center rounded-lg bg-brand-700 px-4 text-body-sm font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800 hover:text-white" data-id="element-2425" data-mp="1koz5k17zmqz2">
                Get started
              </Link>
            </>}
        </div>

        <button type="button" onClick={() => setOpen(current => !current)} aria-expanded={open} aria-label={open ? 'Close menu' : 'Open menu'} className={cn('ml-auto flex min-h-touch min-w-touch items-center justify-center rounded-md transition-colors duration-150 ease-out xl:hidden', inverted ? 'text-white' : 'text-ink')} data-id="element-2426" data-mp="11r4snc3qnvbe">
          {open ? <XIcon aria-hidden className="h-6 w-6" data-id="element-2427" data-mp="w5x46e81etmu" /> : <MenuIcon aria-hidden className="h-6 w-6" data-id="element-2428" data-mp="r5j6nq42b232" />}
        </button>
      </div>

      {open && <div className="max-h-[80dvh] overflow-y-auto border-t border-line bg-surface xl:hidden" data-id="element-2429" data-mp="1629wp8ruj1g0">
          <nav aria-label="Campuzon" className="mx-auto max-w-[88rem] px-4 py-4 sm:px-8" data-id="element-2430" data-mp="1135pgnohfdsb">
            <ul className="space-y-5" data-id="element-2431" data-mp="1evvbgs40gipf">
              {PRIMARY.map(entry => <li key={entry.label} data-id="element-2432" data-mp="1aua5zrtt36p9">
                  {entry.items ? <>
                      <p className="text-caption font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-2433" data-mp="1otjh4d35namd">
                        {entry.label}
                      </p>
                      <ul className="mt-1 divide-y divide-line" data-id="element-2434" data-mp="1jslu9n779sej">
                        {entry.items.map(item => <li key={item.label} data-id="element-2435" data-mp="2v8h9igqq5e6x">
                            <Link to={item.to} onClick={() => setOpen(false)} className="flex min-h-touch-lg items-center text-body text-ink" data-id="element-2436" data-mp="2v765cftda4fa">
                              {item.label}
                            </Link>
                          </li>)}
                      </ul>
                    </> : <Link to={entry.to as string} onClick={() => setOpen(false)} className="flex min-h-touch-lg items-center text-body font-semibold text-ink" data-id="element-2437" data-mp="2hh2pq7ozt8yt">
                      {entry.label}
                    </Link>}
                </li>)}
            </ul>

            <div className="mt-6 flex flex-col gap-2 border-t border-line pt-5" data-id="element-2438" data-mp="1m7z5g2fq9vs5">
              <Link to="/signin" onClick={() => setOpen(false)} className="inline-flex min-h-touch-lg items-center justify-center rounded-lg border border-line text-body font-semibold text-ink" data-id="element-2439" data-mp="1m5wftjihm4ny">
                Sign in
              </Link>
              <Link to="/register" onClick={() => setOpen(false)} className="inline-flex min-h-touch-lg items-center justify-center rounded-lg bg-brand-700 text-body font-semibold text-white hover:text-white" data-id="element-2440" data-mp="139egafxed0h5">
                Get started
              </Link>
            </div>
          </nav>
        </div>}
    </header>;
}